/* eslint-disable @next/next/no-img-element, complexity, sonarjs/cognitive-complexity, react/forbid-component-props */
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { STATUS_ACTIVE, STATUS_RENEW } from '../../../brands/default/constants';
import { SUMMARY } from '../../../brands/default/content/components/checkout';
import { VIP_IMG } from '../../../config/constants/images';
import { formatPrice } from '../../../helpers/currency';
import ThemeContext from '../../../providers/ThemeProvider';
import Tooltip from '../../_generic/tooltip/Tooltip';
import VipSubscribeCheckbox from './VipSubscribeCheckbox';

const Summary = ({
  currencySymbol,
  totalOrderAmount,
  finalOrderAmount,
  totalPostageAmount,
  totalSavingAmount,
  extraClasses,
  isExclusiveProductInBasket,
  totalAdminFee,
  totalPromoCodeDiscount,
  totalWalletAmount,
  totalMerchantAmount,
  basketDiscountForExistingVipUser,
  displaySaving,
  discount,
  giftingSummary,
  totalGiftWrapAmount,
  totalPostageDiscount,
  postageDiscountItemThreshold,
  finalPostageAmount,
  setVipOptIn,
  userVipStatus,
  vipCanShowCheckbox,
  vipDiscountMessage,
  vipOneYearPrice,
  vipOptIn,
  vipOptInBasketDiscount,
  vipOptInFinalOrderAmount,
  vipOptInPrice,
  vipOptInTotalWalletAmount,
  welcomeVipEnabled,
}) => {
  const theme = useContext(ThemeContext);
  const summaryTotal = 'summary__total-price';
  const [displayTotalWalletAmount, setDisplayTotalWalletAmount] =
    useState(totalWalletAmount);

  const hasOtherAmountsApplied =
    totalAdminFee > 0 ||
    totalPostageAmount > 0 ||
    totalPromoCodeDiscount > 0 ||
    totalWalletAmount > 0;

  let finalOrderGrandTotal = finalOrderAmount;
  if (vipCanShowCheckbox && vipOptIn && vipOneYearPrice) {
    // opted in with checkbox
    // removed the addition of VIP because it was messing up wallet purchases
    finalOrderGrandTotal = vipOptInFinalOrderAmount;
  } else if (welcomeVipEnabled && vipOptInFinalOrderAmount) {
    // new opted in grand total
    finalOrderGrandTotal = vipOptInFinalOrderAmount;
  } else {
    finalOrderGrandTotal = finalOrderAmount;
  }

  useEffect(() => {
    if (vipOptInTotalWalletAmount) {
      setDisplayTotalWalletAmount(vipOptInTotalWalletAmount);
    } else {
      setDisplayTotalWalletAmount(totalWalletAmount);
    }
  }, [totalWalletAmount, vipOptInTotalWalletAmount]);

  return (
    <div className={classNames('summary', extraClasses)}>
      <div className="summary__row">
        {vipCanShowCheckbox && (
          <VipSubscribeCheckbox
            currencySymbol={currencySymbol}
            isExclusiveProductInBasket={isExclusiveProductInBasket}
            setVipOptIn={setVipOptIn}
            userVipStatus={userVipStatus}
            vipOneYearPrice={vipOneYearPrice}
          />
        )}
      </div>
      {totalOrderAmount > 0 && hasOtherAmountsApplied && (
        <div className="summary__row">
          <div className="summary__cell">{SUMMARY.subtotal}</div>
          <div className="summary__cell summary__cell-end">
            {currencySymbol}
            {formatPrice(totalOrderAmount, true, false, false)}
          </div>
        </div>
      )}
      {totalAdminFee > 0 && (
        <div className="summary__row">
          <div className="summary__cell">{SUMMARY.adminFee}</div>
          <div className="summary__cell summary__cell-end">
            {currencySymbol}
            {formatPrice(totalAdminFee, true, false, false)}
          </div>
        </div>
      )}
      {giftingSummary?.finalGiftPackNddIncludingPostage > 0 &&
        giftingSummary?.finalGiftPackStdIncludingPostage > 0 && (
          <div className="summary__row">
            <div className="summary__cell">{SUMMARY.giftPack}</div>
            <div className="summary__cell summary__cell-end">
              {currencySymbol}
              {formatPrice(
                giftingSummary.finalGiftPackIncludingPostage,
                true,
                false,
                false,
              )}
            </div>
          </div>
        )}
      {giftingSummary?.finalGiftPackNddIncludingPostage > 0 &&
        giftingSummary?.finalGiftPackStdIncludingPostage <= 0 && (
          <div className="summary__row">
            <div className="summary__cell">{SUMMARY.giftPackExpress}</div>
            <div className="summary__cell summary__cell-end">
              {currencySymbol}
              {formatPrice(
                giftingSummary.finalGiftPackNddIncludingPostage,
                true,
                false,
                false,
              )}
            </div>
          </div>
        )}
      {giftingSummary?.finalGiftPackStdIncludingPostage > 0 &&
        giftingSummary?.finalGiftPackNddIncludingPostage <= 0 && (
          <div className="summary__row">
            <div className="summary__cell">{SUMMARY.giftPackStandard}</div>
            <div className="summary__cell summary__cell-end">
              {currencySymbol}
              {formatPrice(
                giftingSummary.finalGiftPackStdIncludingPostage,
                true,
                false,
                false,
              )}
            </div>
          </div>
        )}
      {totalGiftWrapAmount > 0 && (
        <div className="summary__row">
          <div className="summary__cell">{SUMMARY.giftWrap}</div>
          <div className="summary__cell summary__cell-end">
            {currencySymbol}
            {formatPrice(totalGiftWrapAmount, true, false, false)}
          </div>
        </div>
      )}
      {totalPostageAmount > 0 && (
        <div className="summary__row">
          <div className="summary__cell">
            {SUMMARY.postPackaging}
            {Boolean(postageDiscountItemThreshold) && (
              <Tooltip>
                <div className="summary__tip-title">
                  {SUMMARY.postageCalculated}
                </div>
                <div className="summary__tip-body">
                  {SUMMARY.postageCharge(postageDiscountItemThreshold)}
                </div>
              </Tooltip>
            )}
          </div>
          <div className="summary__cell summary__cell-end">
            {totalPostageDiscount > 0 &&
              Boolean(postageDiscountItemThreshold) && (
                <span className="summary__line-though">
                  ({currencySymbol}
                  {formatPrice(totalPostageAmount, true, false, false)})
                </span>
              )}
            <span>
              {currencySymbol}
              {formatPrice(finalPostageAmount, true, false, false)}
            </span>
          </div>
        </div>
      )}
      {totalPromoCodeDiscount > 0 && (
        <div
          className={classNames('summary__row', {
            'summary__row-border': totalPromoCodeDiscount > 0,
          })}
        >
          <div className="summary__cell">{SUMMARY.promoDiscount(discount)}</div>
          <div className="summary__cell summary__cell-end">
            - {currencySymbol}
            {formatPrice(totalPromoCodeDiscount, true, false, false)}
          </div>
        </div>
      )}
      {displayTotalWalletAmount > 0 && (
        <div className="summary__row">
          <div className="summary__cell">{SUMMARY.creditApplied}</div>
          <div className="summary__cell summary__cell-end">
            {'-'} {currencySymbol}
            {formatPrice(displayTotalWalletAmount, true, false, false)}
          </div>
        </div>
      )}
      {welcomeVipEnabled && (
        <div
          className={classNames('summary__row', summaryTotal, {
            'summary__row-border': hasOtherAmountsApplied,
          })}
        >
          <div className="summary__cell">{SUMMARY.totalToPay}</div>
          <div className="summary__cell summary__cell-end sumary__order-total">
            {currencySymbol}
            {formatPrice(finalOrderAmount, true, true, false)}
          </div>
        </div>
      )}
      {vipDiscountMessage && vipOptInPrice && (
        <div className="summary__row">
          <div className="summary__cell">{vipDiscountMessage}</div>
          <div className="summary__cell summary__cell-end">
            {currencySymbol}
            {formatPrice(vipOptInPrice, true, false, false)}
          </div>
        </div>
      )}
      {vipOptInBasketDiscount > 0 && welcomeVipEnabled && (
        <div className="summary__row">
          <div className="summary__cell">{SUMMARY.vipWelcomeDiscount}</div>
          <div className="summary__cell summary__cell-end">
            -{currencySymbol}
            {formatPrice(vipOptInBasketDiscount, true, false, false)}
          </div>
        </div>
      )}
      {basketDiscountForExistingVipUser > 0 &&
        (userVipStatus === STATUS_ACTIVE || userVipStatus === STATUS_RENEW) && (
          <div className="summary__row">
            <div>
              <img
                alt={SUMMARY.wowcherVipSocialCueAlt}
                className="wowcher-vip-img-summary"
                src={VIP_IMG}
              />
              <span>{SUMMARY.vipDoubleDiscount}</span>
            </div>
            <div className="summary__cell-end">
              - {currencySymbol}
              {formatPrice(
                basketDiscountForExistingVipUser,
                true,
                false,
                false,
              )}
            </div>
          </div>
        )}
      {totalMerchantAmount === 0 && (
        <div
          className={classNames('summary__row', summaryTotal, {
            'summary__row-border': hasOtherAmountsApplied,
          })}
        >
          <div className="summary__cell">
            {welcomeVipEnabled ? SUMMARY.totalToPayVip : SUMMARY.totalToPay}
          </div>

          <div className="summary__cell summary__cell-end sumary__order-total">
            {currencySymbol}
            {formatPrice(finalOrderGrandTotal, true, true, false)}
          </div>
        </div>
      )}
      {displaySaving && totalSavingAmount > 0 && (
        <div className="summary__row">
          <div className="summary__cell">
            <span className="summary__assurance-color">
              <FontAwesomeIcon className="summary__check-icon" icon={faCheck} />
              {SUMMARY.congratulations}
            </span>
            {SUMMARY.youSaved}
          </div>
          <div className="summary__cell summary__cell-end summary__assurance-color">
            {currencySymbol}
            {formatPrice(totalSavingAmount, true, false, false)}
          </div>
        </div>
      )}
      {totalMerchantAmount > 0 && (
        <div
          className={classNames('summary__row', summaryTotal, {
            'summary__row-border': hasOtherAmountsApplied,
          })}
        >
          <div className="summary__cell">{SUMMARY.totalPayToday}</div>
          <div className="summary__cell summary__cell-end sumary__order-total">
            {currencySymbol}
            {formatPrice(finalOrderGrandTotal, true, true)}
          </div>
        </div>
      )}
      {totalMerchantAmount > 0 && (
        <div className="summary__row">
          <div className="summary__cell">
            <span>{SUMMARY.balanceOf}</span>
            <span className="summary__total-merchant">
              {currencySymbol}
              {formatPrice(totalMerchantAmount, true, false, false)}
            </span>
            <span>{SUMMARY.merchant}</span>
          </div>
        </div>
      )}
      <style jsx>{`
        .summary__row {
          display: flex;
          color: #333;
          position: relative;
        }
        .wowcher-vip-img-summary {
          height: 25px;
          padding-right: 5px;
        }
        .summary__cell {
          line-height: 30px;
        }
        .summary__cell-end {
          font-weight: bold;
          margin-left: auto;
        }
        .summary__row-border {
          border-top: 1px solid #ccc;
        }
        .summary__total-price {
          font-size: 2rem;
        }
        .summary__total-price > .summary__cell {
          line-height: 40px;
        }
        .sumary__order-total {
          color: ${theme.colors.primary};
          font-weight: 700;
        }
        .summary__total-merchant {
          color: ${theme.colors.primary};
          font-weight: bold;
        }
        .summary__cell :global(.summary__check-icon) {
          width: 18px;
          height: 18px;
          padding-right: 5px;
        }
        .summary__assurance-color {
          color: #01b901;
        }
        .summary__postage-discount {
          font-size: 85%;
          font-style: italic;
        }
        .summary__line-though {
          font-style: italic;
          text-decoration: line-through;
          padding-right: 10px;
        }
        .summary__tip-title {
          font-weight: bold;
          margin-bottom: 6px;
        }
        .summary__tip-body {
          line-height: 1.5;
        }
        .summary :global(.tool-tip) {
          background-color: ${theme.colors.white};
          border: 2px solid ${theme.colors.charcoal};
          line-height: 1.6;
        }
        .summary :global(.tool-tip__i) {
          color: ${theme.colors.charcoal};
          font-style: normal;
          line-height: normal;
          top: 1px;
          left: -1px;
        }
        .summary :global(.tool-tip__text) {
          text-align: left;
          background-color: ${theme.colors.notificationbluelight};
          color: ${theme.colors.notificationblue};
          border-color: ${theme.colors.notificationbluedark};
          border-radius: 0;
        }
        .summary :global(.tool-tip__text):before {
          border-color: ${theme.colors.notificationbluedark} transparent;
        }
        .summary :global(.tool-tip__text):after {
          border-color: ${theme.colors.notificationbluelight} transparent;
        }
        @media (max-width: ${theme.breakpoints.mdDown}) {
          .summary :global(.tool-tip__text) {
            top: -140px;
          }
        }
        @media (min-width: ${theme.breakpoints.mdUp}) {
          .tool-tip {
            position: absolute;
            top: 6px;
            left: -24px;
          }
          .summary :global(.tool-tip__text) {
            left: -215px;
            top: -38px;
          }
          .summary :global(.tool-tip__text):before,
          .summary :global(.tool-tip__text):after {
            transform: rotate(90deg);
          }
          .summary :global(.tool-tip__text):before {
            border-color: ${theme.colors.notificationbluedark} transparent;
            top: 40px;
            left: 194px;
          }
          .summary :global(.tool-tip__text):after {
            border-color: ${theme.colors.notificationbluelight} transparent;
            left: 192px;
            top: 40px;
          }
        }
      `}</style>
    </div>
  );
};

Summary.propTypes = {
  basketDiscountForExistingVipUser: PropTypes.number,
  currencySymbol: PropTypes.string,
  discount: PropTypes.number,
  displaySaving: PropTypes.bool,
  extraClasses: PropTypes.string,
  finalOrderAmount: PropTypes.number,
  finalPostageAmount: PropTypes.number,
  giftingSummary: PropTypes.object,
  isExclusiveProductInBasket: PropTypes.bool,
  postageDiscountItemThreshold: PropTypes.number,
  setVipOptIn: PropTypes.func,
  totalAdminFee: PropTypes.number,
  totalGiftWrapAmount: PropTypes.number,
  totalMerchantAmount: PropTypes.number,
  totalOrderAmount: PropTypes.number,
  totalPostageAmount: PropTypes.number,
  totalPostageDiscount: PropTypes.number,
  totalPromoCodeDiscount: PropTypes.number,
  totalSavingAmount: PropTypes.number,
  totalWalletAmount: PropTypes.number,
  userVipStatus: PropTypes.string,
  vipCanShowCheckbox: PropTypes.bool,
  vipDiscountMessage: PropTypes.element,
  vipOneYearPrice: PropTypes.number,
  vipOptIn: PropTypes.bool,
  vipOptInBasketDiscount: PropTypes.number,
  vipOptInFinalOrderAmount: PropTypes.number,
  vipOptInPrice: PropTypes.number,
  vipOptInTotalWalletAmount: PropTypes.number,
  welcomeVipEnabled: PropTypes.bool,
};

Summary.defaultProps = {
  currencySymbol: '',
  discount: 0,
  displaySaving: false,
  extraClasses: '',
  finalOrderAmount: 0,
  postageDiscountItemThreshold: 0,
  totalAdminFee: 0,
  totalMerchantAmount: 0,
  totalOrderAmount: 0,
  totalPostageAmount: 0,
  totalPromoCodeDiscount: 0,
  totalSavingAmount: 0,
  totalWalletAmount: 0,
};

export default Summary;
